import type { StateCreator } from 'zustand';
import type { CurrencyApi } from '../../graphql';
import { Currency } from '../../graphql';
import type { GlobalStore } from './store';
import { globalStore, useGlobalStore } from './store';

/** Defines the conversion rates for a single base currency. */
export type CurrencyRates = CurrencyApi;

export type MonetaryValue = [amount: number, currency: Currency];

export function parseCurrency(name: string): Currency {
  const upper = name.toUpperCase();
  if (Currency[upper as keyof typeof Currency]) {
    return upper as Currency;
  }
  throw new Error(`Invalid currency: ${name}`);
}

export type CurrencyRatesSlice = {
  currencyRates: CurrencyApi | null;
  setCurrencyRates: (
    currency: CurrencyApi['baseCurrency'],
    rates: CurrencyApi['rates'],
  ) => Promise<void>;
};

export const createCurrencyRateSlices: StateCreator<CurrencyRatesSlice> = (
  setState,
) => ({
  currencyRates: null,
  setCurrencyRates: async (
    currency: CurrencyApi['baseCurrency'],
    rates: CurrencyApi['rates'],
  ) => {
    setState({ currencyRates: { rates, baseCurrency: currency } });
  },
});

/*
 * Memoized selectors
 * https://docs.pmnd.rs/zustand/recipes/recipes#memoizing-selectors
 */
const currencyRatesSelector = (s: GlobalStore) => s.currencyRates;
const setCurrencyRatesSelector = (s: GlobalStore) => s.setCurrencyRates;

export function useCurrencyRates() {
  return useGlobalStore(currencyRatesSelector);
}

export function useSetCurrencyRates() {
  return useGlobalStore(setCurrencyRatesSelector);
}

/** Converts the given monetary value into the currency of targetRates.baseCurrency */
export function convertMonetaryValue(
  amount: MonetaryValue[0],
  currency: MonetaryValue[1],
  targetRates: CurrencyRates,
): number;
export function convertMonetaryValue(
  amount: MonetaryValue[0],
  currency: MonetaryValue[1],
  targetRates: null,
): null;
export function convertMonetaryValue(
  amount: MonetaryValue[0],
  currency: MonetaryValue[1],
  targetRates: CurrencyRates | null,
): number | null;
export function convertMonetaryValue(
  amount: MonetaryValue[0],
  currency: MonetaryValue[1],
  targetRates: CurrencyRates | null,
): number | null {
  if (!targetRates) {
    console.warn('convertMonetaryValue called with no currency rates');
    return null;
  }
  const rate =
    currency === targetRates.baseCurrency
      ? 1
      : targetRates?.rates?.find((r) => r?.code === currency)?.value;
  if (!rate) {
    console.error(
      `Currency rate not available: ${currency}->${targetRates.baseCurrency}`,
    );
    return amount;
  }
  return amount / Number(rate);
}

/**
 * Utility functions
 */
export function convertToDefaultCurrency(amount: number, _currency?: string) {
  // State doesn't work in worker threads.
  if (globalThis.window === undefined)
    throw new Error('convertToDefaultCurrency called in worker thread');

  const currency = _currency?.toUpperCase();
  const state = globalStore.getState();

  if (!currency || currency === state.defaultCurrency) return amount;

  const rates = state.currencyRates?.rates;
  if (!rates) {
    console.error('convertToDefaultCurrency called with no currency rates');
    return null;
  }

  const rate = rates.find((r) => r?.code === currency)?.value;

  if (!rate) {
    console.error('rate not available for currency', currency);
    return amount;
  }

  return amount / rate;
}

export function getCurrencyRate(rate: Currency | string) {
  // State doesn't work in worker threads.
  if (globalThis.window === undefined)
    throw new Error('getCurrencyRate called in worker thread');

  const { currencyRates } = globalStore.getState();

  if (!currencyRates) {
    console.error('getCurrencyRate called with no currency rates');
    return null;
  }

  return currencyRates?.rates?.find((r) => r?.code === rate)?.value;
}
